import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getSortedRowModel,
    getPaginationRowModel
} from "@tanstack/react-table";
import { useMemo } from "react";
import UpIcon from "../icons/up";
import DownIcon from "../icons/down";
import LeftIcon from "../icons/left";
import RightIcon from "../icons/right";
import { classNames } from "../App"

export default function CustomDataTable({ data, columns, pagination, sorting, onSortingChange, onPaginationChange, isSortable = true, isPaginable = true, className, getRowClassName }) {

    const dynamicClasses = {
        'mobile': 'hidden sm:table-cell',
        'tablet': 'hidden md:table-cell',
        'desktop': 'hidden lg:table-cell',
        'none': 'table-cell'
    }

    // Pre-sort data to ensure HR-claimed items are always first
    // This ensures HR-claimed slots appear at the top of the table regardless of pagination or column sorting
    const preSortedData = useMemo(() => {
        if (!data || data.length === 0) return data;
        // Separate HR-claimed from others
        const hrClaimed = data.filter(item => item?.is_hr_claimed);
        const notHrClaimed = data.filter(item => !item?.is_hr_claimed);
        // HR-claimed items first, then others - this ensures they're always at the top
        return [...hrClaimed, ...notHrClaimed];
    }, [data]);

    const table = useReactTable({
        data: preSortedData,
        columns,
        manualPagination: false, // Enable server-side pagination
        manualSorting: false, // Set to true for server-side sorting
        enableMultiSort: true,
        state: {
            sorting,
            pagination,
        },
        onSortingChange: onSortingChange, // Called when sorting changes
        onPaginationChange: onPaginationChange, // Called when pagination changes
        getCoreRowModel: getCoreRowModel(), // Basic row model
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <div className={classNames("bg-white p-4 sm:p-8 rounded-xl", className)}>
            <div className="overflow-x-auto">
            <table className="w-full border-gray-300 border-collapse">
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className={classNames('border-gray-300 px-4 pb-4', dynamicClasses[header.column.columnDef.visibleTo || 'none'])}
                                // className={classNames('border-gray-300 px-4 pb-4', `hidden md:table-cell`)}
                                >
                                    {header.isPlaceholder ? null : (
                                        <div
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={header.column.getToggleSortingHandler()} // Correct sorting handler
                                            className={`flex items-center ${isSortable && "cursor-pointer"}`}
                                        >
                                            <span className="font-subtitle-semibold text-neutral-500">
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </span>
                                            {isSortable && header.column.getCanSort() && (
                                                <>
                                                    {header.column.getIsSorted() === "asc" && (
                                                        <UpIcon className="size-2 mb-2 ml-1" />
                                                    )}
                                                    {header.column.getIsSorted() === "desc" && (
                                                        <DownIcon className="size-2 mt-2 ml-1" />
                                                    )}
                                                    {!header.column.getIsSorted() &&
                                                        <div className="ml-1">
                                                            <UpIcon className="size-2" />
                                                            <DownIcon className="size-2" />
                                                        </div>
                                                    }
                                                </>
                                            )}
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className={getRowClassName && row.original ? getRowClassName(row.original) : ''}>
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id}
                                    className={classNames('border-t border-b px-4 py-4', dynamicClasses[cell.column.columnDef.visibleTo || 'none'])}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>

            {isPaginable && <div className="flex justify-between items-center mt-4">
                <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className={classNames("px-4 py-2 bg-gray-200 rounded-lg bg-transparent border border-neutral-border",
                        table.getCanPreviousPage() && "cursor-pointer text-neutral-800 fill-neutral-800",
                        !table.getCanPreviousPage() && "cursor-not-allowed text-neutral-400 fill-neutral-400"
                    )}
                >
                    <span className="flex flex-row"><LeftIcon className="size-4 relative top-[0.35rem] " /> <span className="ml-2">Previous</span> </span>
                </button>
                <span className="font-poppins text-neutral-400">
                    Page {pagination.pageIndex + 1}
                </span>
                <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className={classNames("px-4 py-2 bg-gray-200 rounded-lg bg-transparent border border-neutral-border",
                        table.getCanNextPage() && "cursor-pointer text-neutral-800 fill-neutral-800",
                        !table.getCanNextPage() && "cursor-not-allowed text-neutral-400 fill-neutral-400"
                    )}
                >
                    <span className="flex flex-row"><span className="mr-2">Next</span><RightIcon className="size-4 relative top-[0.35rem]" /> </span>
                </button>
            </div>}
        </div>
    );
}